// docs/102 FL — a flier's move is a flight, not a walk. Which creatures fly is content
// (content/creatures/fliers.json, by the appearance the sim gives the unit); this module answers
// "does it fly", and draws a flight as pure numbers so it can be tested without Phaser:
// it pushes off, crosses the board as the crow flies on an arc (its shadow on the ground under it,
// fading in as it rises and out as it comes down) and lands on the square the sim's walk ended on.
// `EffectPlayer` turns these into the figure's motion; `FlightFx` draws the shadow, dust and air.
import table from '../../../../../content/creatures/fliers.json';
import type { Effect, Tile } from '../sim/types';
import { feetPoint } from './walk';

export type Wings = 'small' | 'great';
export interface Flier { appearance: number; name: string; wings: Wings; inPlay?: string }

const FLIERS: ReadonlyMap<number, Flier> = new Map((table.fliers as Flier[]).map((f) => [f.appearance, f]));

/** The flier drawn with this appearance, if it flies. */
export function flierOf(appearance: number | undefined): Flier | undefined {
  return appearance === undefined ? undefined : FLIERS.get(appearance);
}

/** Whether a unit with this appearance flies when it moves. */
export const flies = (appearance: number | undefined): boolean => flierOf(appearance) !== undefined;

/** Every tagged flier (tests, the docs). */
export const allFliers = (): Flier[] => [...FLIERS.values()];

/** How far a flight goes as the crow flies, in squares (between the body's origins). */
export function crowSquares(from: Tile, to: Tile): number { return Math.hypot(to.x - from.x, to.y - from.y); }

/** How long a flight of `squares` takes at 1× (ms): a hop is quick, a long flight never drags. */
export function flightMs(squares: number, size = 1): number {
  const ms = Math.min(1700, Math.max(640, 460 + 120 * squares));
  return Math.round(size >= 2 ? ms * 1.15 : ms);
}

/** How high the arc rises at its top (board pixels): higher for a longer flight and a bigger body. */
export function flightPeak(squares: number, size: number, cell: number): number {
  const share = Math.min(2.4, Math.max(0.7, 0.5 + 0.28 * squares));
  return cell * share * (size >= 2 ? 1 + 0.25 * (size - 1) : 1);
}

/** The shadow at its darkest (straight under a flier at the top of its arc it is lighter than that, FL4). */
export const SHADOW_ALPHA = 0.28;

export interface FlightPose {
  /** The point on the ground under it (its feet point were it standing there). */
  ground: { x: number; y: number };
  /** How high it is off the ground (board pixels, ≥ 0). */
  lift: number;
  /** 0 on the ground .. 1 at the top of its arc. */
  height: number;
  /** The shadow's alpha and scale. */
  shadowAlpha: number;
  shadowScale: number;
}

const clamp01 = (v: number): number => Math.min(1, Math.max(0, v));
/** Smoothstep. */
const smooth = (v: number): number => { const t = clamp01(v); return t * t * (3 - 2 * t); };

/** How far along the ground it has come at `k` (0..1 of the clip): it rises before it sets off and settles after it arrives. */
export function groundShare(k: number): number { return smooth((k - 0.06) / 0.86); }

/** How high it is at `k`, 0..1 of the peak: a quick push off, a long glide, a quick drop onto its square. */
export function heightAt(k: number): number {
  const t = clamp01(k);
  return Math.pow(Math.sin(Math.PI * t), 0.6);
}

/**
 * Where a flier is `k` (0..1) through a flight from origin `from` to origin `to`: the feet point
 * under it and its lift, with a light wingbeat bob while it is up. The shadow fades in with the
 * height (none at all on the ground) and shrinks a little the higher it goes.
 */
export function flightPose(k: number, from: Tile, to: Tile, size: number, cell: number, peak = flightPeak(crowSquares(from, to), size, cell)): FlightPose {
  const a = feetPoint(from, size, cell), b = feetPoint(to, size, cell);
  const s = groundShare(k);
  const h = heightAt(k);
  const beats = size >= 2 ? 3 : 5;
  const bob = h > 0.3 ? Math.sin(clamp01(k) * Math.PI * 2 * beats) * cell * 0.05 * Math.min(1, size) * h : 0;
  return {
    ground: { x: a.x + (b.x - a.x) * s, y: a.y + (b.y - a.y) * s },
    lift: Math.max(0, peak * h + bob),
    height: h,
    // it fades in over the first quarter of the flight and out over the last, never popping
    shadowAlpha: SHADOW_ALPHA * smooth(k / 0.25) * smooth((1 - k) / 0.25) * (1 - 0.3 * h),
    shadowScale: 1 - 0.3 * h,
  };
}

/** docs/102 FL5: when the carrier seizes what it carries off, lets go of it, and it hits the ground (0..1 of the flight). */
export const CARRY_GRAB_K = 0.1, CARRY_LET_GO_K = 0.76, CARRY_DROP_K = 0.88;

/**
 * The carrier's grip, 0..1: it closes quickly as it rises (0 before it seizes the figure) and opens
 * at the far end, the figure dropping faster and faster the rest of the way (0 once it lands).
 */
export function gripAt(k: number): number {
  const up = smooth((k - CARRY_GRAB_K) / 0.12);
  const open = clamp01((k - CARRY_LET_GO_K) / (CARRY_DROP_K - CARRY_LET_GO_K));
  return up * (1 - open * open);
}

/**
 * Where the carried figure is `k` through the carrier's flight. On its own it goes along the ground
 * from where it stood to where the sim set it down; in the grip it is under the carrier, hanging
 * `hang` px below its feet (so it leaves the ground once the carrier is higher than that); between
 * the two it is drawn from one to the other, so it is snatched up and let fall. `carrier` is the
 * ground point under the carrier and `carrierLift` its height. Returns its feet point as drawn
 * (`x`, `y`), the ground under it, its lift and the grip.
 */
export function carriedPose(k: number, carrier: { x: number; y: number }, carrierLift: number, from: Tile, to: Tile, size: number, cell: number, hang: number): { x: number; y: number; ground: { x: number; y: number }; lift: number; grip: number } {
  const a = feetPoint(from, size, cell), b = feetPoint(to, size, cell);
  const s = smooth((k - CARRY_GRAB_K) / (CARRY_DROP_K - CARRY_GRAB_K));
  const own = { x: a.x + (b.x - a.x) * s, y: a.y + (b.y - a.y) * s };
  const g = gripAt(k);
  const ground = { x: own.x + (carrier.x - own.x) * g, y: own.y + (carrier.y - own.y) * g };
  // it never hangs below the ground: a carrier still low drags it along the grass
  const lift = Math.max(0, (carrierLift - hang) * g);
  return { x: ground.x, y: ground.y - lift, ground, lift, grip: g };
}

/** docs/102 FL5: a carrier climbs higher with its prey, so the figure hanging from it clears the ground. */
export const CARRY_PEAK = 1.5;

/** A Moved that a flier plays as a flight: it goes somewhere, with steps (a Blink or a burrow, `costs` empty, is not a flight). */
export function isFlight(e: Effect, appearance: number | undefined): e is Extract<Effect, { kind: 'Moved' }> {
  if (e.kind !== 'Moved' || !e.path.length || !flies(appearance)) return false;
  if (e.costs && e.costs.length === 0) return false;
  const from = e.from, to = e.path[e.path.length - 1]!;
  return !from || from.x !== to.x || from.y !== to.y;
}

/** docs/102 FL5: a flier's flight carries a figure off with it — who, from where and to where. */
export interface Carry { unit: number; from: Tile; to: Tile }
